import React, { useState, useEffect } from 'react';
import { productAPI, categoryAPI } from '../../services/api';
import { getImgUrl } from '../../utils/config';
import { Plus, Edit, Trash2, X } from 'lucide-react';

const AdminProducts = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentProduct, setCurrentProduct] = useState(null);
    const [formData, setFormData] = useState({
        name: '', description: '', short_description: '', badge: '', spiceLevel: '', origin: '',
        price: '', originalPrice: '', stock: '', image: '', status: 'Active', categoryId: ''
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchProducts();
        fetchCategories();
    }, []);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const response = await productAPI.getAllAdmin();
            setProducts(response.data.products);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await categoryAPI.getAllAdmin();
            setCategories(response.data.categories || []);
        } catch (error) {
            console.error("Failed to fetch categories", error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = new FormData();
            data.append('name', formData.name);
            data.append('description', formData.description);
            data.append('short_description', formData.short_description);
            data.append('badge', formData.badge);
            data.append('spiceLevel', formData.spiceLevel);
            data.append('origin', formData.origin);
            data.append('status', formData.status);

            const variation = {
                categoryId: parseInt(formData.categoryId),
                price: parseFloat(formData.price),
                originalPrice: parseFloat(formData.originalPrice),
                // stock: parseInt(formData.stock) // Note: Backend Stocks are separate, but we might need to handle stock creation separately or assume backend handles it? 
                // Wait, ProductController DOES NOT create stock. StockController does. 
                // For now, let's focus on Product creation. Stock might be 0 initially.
            };

            // If updating, include the variation ID to prevent duplicate error
            if (currentProduct && currentProduct.variations && currentProduct.variations.length > 0) {
                variation.id = currentProduct.variations[0].id;
            }

            data.append('variations', JSON.stringify([variation]));

            if (formData.image && formData.image.length > 0) {
                // Handle multiple images
                // If it's an array of Files (new upload)
                if (Array.isArray(formData.image) && formData.image[0] instanceof File) {
                    formData.image.forEach((file) => {
                        const newFile = new File([file], `0_${file.name}`, { type: file.type });
                        data.append('images', newFile);
                    });
                } else if (formData.image instanceof File) {
                    // Fallback for single file (shouldn't happen with new logic but good for safety)
                    const newFile = new File([formData.image], `0_${formData.image.name}`, { type: formData.image.type });
                    data.append('images', newFile);
                }
            }

            if (currentProduct) {
                await productAPI.update(currentProduct.id, data);
            } else {
                await productAPI.create(data);
            }
            fetchProducts();
            closeModal();
        } catch (error) {
            console.error("Operation failed", error);
            alert("Operation failed: " + (error.response?.data?.error || error.message));
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this product?')) {
            try {
                await productAPI.delete(id);
                fetchProducts();
            } catch (error) {
                console.error(error);
                alert("Delete failed");
            }
        }
    };

    const requestEdit = (product) => {
        setCurrentProduct(product);
        // Map backend product structure to flat form data
        // Assuming first variation for simplicity in this Basic Admin UI
        const firstVar = product.variations?.[0] || {};

        let existingImages = firstVar.image || [];
        // Ensure it's an array (handle stringified JSON case if backend sends that, though Products.js handles it, Admin might get raw)
        if (typeof existingImages === 'string') {
            try { existingImages = JSON.parse(existingImages); } catch { existingImages = []; }
        }


        setFormData({
            name: product.name,
            description: product.description,
            short_description: product.short_description || '',
            badge: product.badge || '',
            spiceLevel: product.spiceLevel || '',
            origin: product.origin || '',
            status: product.status || 'Active',

            price: firstVar.price || '',
            originalPrice: firstVar.originalPrice || '',
            categoryId: firstVar.categoryId || '',
            // Keep existing images as simple array for reference, but file input will be empty
            image: existingImages
        });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setCurrentProduct(null);
        setFormData({
            name: '', description: '', short_description: '', badge: '', spiceLevel: '', origin: '',
            price: '', originalPrice: '', stock: '', image: [], status: 'Active', categoryId: ''
        });
    };

    return (
        <div className="pt-24 min-h-screen bg-gray-100 px-6 pb-12">
            <div className="container mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-800">Product Management</h1>
                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 shadow-md transition-colors"
                    >
                        <Plus size={20} />
                        <span>Add Product</span>
                    </button>
                </div>

                <div className="bg-white rounded-xl shadow-md overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Origin</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Badge</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {products.map((product) => (
                                <tr key={product.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="h-10 w-10 flex-shrink-0">
                                                {/* Display first variation image */}
                                                <img className="h-10 w-10 rounded-full object-cover"
                                                    src={getImgUrl(product.variations?.[0]?.image?.[0]) || 'https://via.placeholder.com/40'}
                                                    alt="" />
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                                <div className="text-sm text-gray-500 truncate max-w-xs">{product.short_description}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {product.origin}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {product.badge}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${product.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                            }`}>
                                            {product.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button onClick={() => requestEdit(product)} className="text-indigo-600 hover:text-indigo-900 mr-4">
                                            <Edit size={18} />
                                        </button>
                                        <button onClick={() => handleDelete(product.id)} className="text-red-600 hover:text-red-900">
                                            <Trash2 size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto pt-10 pb-10">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl relative">
                        <button onClick={closeModal} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                            <X size={24} />
                        </button>
                        <h2 className="text-2xl font-bold mb-6 text-gray-800">{currentProduct ? 'Edit Product' : 'Add New Product'}</h2>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Name</label>
                                    <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm border p-2" required />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Badge (e.g., Best Seller)</label>
                                    <input type="text" value={formData.badge} onChange={(e) => setFormData({ ...formData, badge: e.target.value })} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm border p-2" required />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Short Description</label>
                                <input type="text" value={formData.short_description} onChange={(e) => setFormData({ ...formData, short_description: e.target.value })} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm border p-2" required />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Description</label>
                                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm border p-2" rows="3" required></textarea>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Category</label>
                                    <select value={formData.categoryId} onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm border p-2" required>
                                        <option value="">Select Category</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    {/* Removed Spice Level */}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Status</label>
                                    <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm border p-2">
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                    </select>
                                </div>
                                {/* Removed Origin */}
                            </div>

                            <div className="border-t pt-4 mt-4">
                                <h3 className="text-lg font-medium text-gray-900 mb-2">Pricing & Variation</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Price (Selling Price)</label>
                                        <input type="number" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm border p-2" required />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Original Price (MRP)</label>
                                        <input type="number" value={formData.originalPrice} onChange={(e) => setFormData({ ...formData, originalPrice: e.target.value })} className="mt-1 block w-full border-gray-300 rounded-md shadow-sm border p-2" required />
                                    </div>
                                </div>

                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-700">Product Images</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={(e) => setFormData({ ...formData, image: Array.from(e.target.files) })}
                                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm border p-2"
                                    />
                                    {formData.image && Array.isArray(formData.image) && formData.image.length > 0 && formData.image[0] instanceof File ? (
                                        <p className="text-xs text-gray-500 mt-1">Selected: {formData.image.length} files</p>
                                    ) : (
                                        currentProduct && formData.image && formData.image.length > 0 && (
                                            <p className="text-xs text-gray-500 mt-1">Current Images: {formData.image.length}</p>
                                        )
                                    )}
                                </div>
                            </div>

                            <button type="submit" className="w-full bg-green-600 text-white py-2 rounded-md hover:bg-green-700 font-semibold mt-4">
                                {currentProduct ? 'Update Product' : 'Create Product'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminProducts;
